import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, Group, Select, Stack, Switch, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { useBranches } from '../branches/branchesApi';
import type { DeviceDto } from './types';
import { useCreateDevice, useUpdateDevice } from './devicesApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** When present we edit; when null we create. */
  device: DeviceDto | null;
}

interface FormValues {
  name: string;
  branchId: string;
  serial: string;
  isActive: boolean;
}

export function DeviceFormDrawer({ opened, onClose, device }: Props) {
  const { t, i18n } = useTranslation();
  const { data: branches } = useBranches();
  const create = useCreateDevice();
  const update = useUpdateDevice();
  const submitting = create.isPending || update.isPending;
  const editing = device !== null;
  // Drawer doesn't honour logical positions; flip per direction so it
  // always slides in from the inline-end edge.
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const branchOptions = (branches ?? []).map((b) => ({ value: b.branchId, label: b.nameAr }));

  const form = useForm<FormValues>({
    initialValues: { name: '', branchId: '', serial: '', isActive: true },
    validate: {
      name: (v) => (v.trim() ? null : t('common.errors.required')),
      branchId: (v) => (editing || v ? null : t('common.errors.required')),
    },
  });

  // Reset the form whenever we open in a different mode.
  useEffect(() => {
    if (!opened) return;
    form.setValues({
      name: device?.name ?? '',
      branchId: device?.branchId ?? '',
      serial: device?.serial ?? '',
      isActive: device?.isActive ?? true,
    });
    form.resetDirty();
    // We intentionally don't include `form` in deps — that'd loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, device]);

  const submit = async (values: FormValues) => {
    const name = values.name.trim();
    const serial = values.serial.trim() || null;
    try {
      if (editing) {
        await update.mutateAsync({
          id: device.deviceId,
          request: { name, serial, isActive: values.isActive },
        });
      } else {
        await create.mutateAsync({ name, branchId: values.branchId, serial });
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: name,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('devices.form.editTitle') : t('devices.form.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <TextInput
            label={t('devices.form.nameLabel')}
            placeholder={t('devices.form.namePlaceholder')}
            withAsterisk
            {...form.getInputProps('name')}
          />
          {!editing && (
            <Select
              label={t('devices.form.branchLabel')}
              placeholder={t('devices.form.branchPlaceholder')}
              data={branchOptions}
              searchable
              withAsterisk
              comboboxProps={{ withinPortal: true }}
              {...form.getInputProps('branchId')}
            />
          )}
          <TextInput
            label={t('devices.form.serialLabel')}
            placeholder={t('devices.form.serialPlaceholder')}
            {...form.getInputProps('serial')}
          />
          {editing && (
            <Switch
              label={t('devices.form.activeLabel')}
              description={t('devices.form.activeHelp')}
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
